import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import axios from 'axios';

export default function DynamicFormDisplay() {
  const { formName } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const response = await axios.get(
          `${baseUrl}/api/dynamic-forms/name/${formName}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        if (response.data.success) {
          setFormData(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load form');
        }
      } catch (err) {
        setError(err.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    if (formName) {
      fetchFormData();
    }
  }, [formName]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading form...</Typography>
      </Box>
    );
  }
  if (error || !formData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        <Typography>{error || 'Form not found'}</Typography>
      </Box>
    );
  }

  // Top-level fields except List, formName, and meta fields
  const topLevelFields = Object.entries(formData)
    .filter(([key]) =>
      key !== 'List' &&
      key !== 'formName' &&
      key !== '_id' &&
      key !== 'updatedAt' &&
      key !== 'createdAt' &&
      key !== '__v'
    )
    .map(([key, value]) => ({ key, value }));
  const topLevelFieldNames = topLevelFields.map(f => f.key);
  const list = formData.List || [];

  // Custom UI for date and time fields (if present)
  const dateField = topLevelFields.find(f => f.key.toLowerCase() === 'date');
  const timeField = topLevelFields.find(f => f.key.toLowerCase().includes('time'));

  return (
  <Box sx={{ minWidth: 900, maxWidth: 1100, mx: 'auto', mt: 3, mb: 4, background: '#fff', borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
    {/* Header - maroon background, centered, more rounded top corners, smaller height */}
    <Box sx={{ background: '#8a1538', color: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18, py: 2, textAlign: 'center', fontSize: '1.45rem', fontWeight: 700, letterSpacing: 1 }}>
      {formData.formName ? `${formData.formName}` : 'Checklist'}
    </Box>

    {/* Custom date/time row UI (like image) */}
    {dateField && timeField && (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, px: 3, pt: 3, alignItems: 'flex-end' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#607080', fontWeight: 500, fontSize: '1.1rem', mb: 1 }}>Date</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <input
              type="text"
              value={dateField.value ? new Date(dateField.value).toLocaleDateString('en-GB') : ''}
              readOnly
              placeholder="dd-mm-yyyy"
              style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #e0e0e0', fontSize: '1.15rem', background: '#fafafa', color: '#222', fontWeight: 500, letterSpacing: 1 }}
            />
            <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#222', fontSize: '1.3rem' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M7 11h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-9 8h10a2 2 0 002-2V7a2 2 0 00-2-2h-1V3h-2v2H9V3H7v2H6a2 2 0 00-2 2v10a2 2 0 002 2zm0-2V7h10v10H6z" fill="#222"/></svg>
            </span>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#607080', fontWeight: 500, fontSize: '1.1rem', mb: 1 }}>Time In</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <input
              type="text"
              value={timeField.value}
              readOnly
              style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #e0e0e0', fontSize: '1.15rem', background: '#fafafa', color: '#222', fontWeight: 500, letterSpacing: 1 }}
            />
            <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#222', fontSize: '1.3rem' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M12 7v5l4 2" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke="#222" strokeWidth="2"/></svg>
            </span>
          </Box>
        </Box>
      </Box>
    )}
    {/* Top fields: first section with empty header, show as row of input boxes */}
    {list.length > 0 && list[0].header.trim() === '' && (
      <Box sx={{ px: 3, pt: 3 }}>
        {Array.from({ length: Math.ceil(list[0].items.length / 3) }).map((_, rowIdx) => (
          (() => {
            // Collect items for this row
            const items = list[0].items.slice(rowIdx * 3, rowIdx * 3 + 3);
            // If any item is a radio, render it as a full-width row
            if (items.some(item => Array.isArray(item.options) && item.options.length > 1 && typeof item.value === 'string' && item.options.includes(item.value))) {
              return items.map((item, idx) => {
                if (Array.isArray(item.options) && item.options.length > 1 && typeof item.value === 'string' && item.options.includes(item.value)) {
                  return (
                    <Box key={idx} sx={{ width: '100%', mb: 2 }}>
                      <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {item.options.map((opt, oidx) => (
                          <label key={oidx} style={{ color: '#8a1538', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontSize: '1.05rem' }}>
                            <input
                              type="radio"
                              checked={item.value === opt}
                              readOnly
                              style={{ accentColor: '#8a1538', marginRight: 6 }}
                            />
                            {opt}
                          </label>
                        ))}
                      </Box>
                    </Box>
                  );
                }
                // Otherwise, skip rendering in this row
                return null;
              });
            }
            // Otherwise, render text/checkbox fields in 2 columns per row
            return (
              <Box key={rowIdx} sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                {items.slice(0, 2).map((item, idx) => {
                  // Use type for rendering
                  if (item.type === 'checkbox') {
                    if (Array.isArray(item.value) && Array.isArray(item.options) && item.options.length > 0) {
                      // Checkbox group
                      return (
                        <Box key={idx} sx={{ flex: 1, minWidth: 180 }}>
                          <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {item.options.map((opt, oidx) => (
                              <label key={oidx} style={{ color: '#8a1538', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontSize: '1.05rem' }}>
                                <input
                                  type="checkbox"
                                  checked={item.value.includes(opt)}
                                  readOnly
                                  style={{ accentColor: '#8a1538', marginRight: 6 }}
                                />
                                {typeof opt === 'string' ? opt.toUpperCase() : opt}
                              </label>
                            ))}
                          </Box>
                        </Box>
                      );
                    } else {
                      // Single checkbox
                      return (
                        <Box key={idx} sx={{ flex: 1, minWidth: 180 }}>
                          <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                          <input
                            type="checkbox"
                            checked={item.value === true || item.value === 'true'}
                            readOnly
                            style={{ accentColor: '#8a1538', marginRight: 6 }}
                          />
                        </Box>
                      );
                    }
                  }
                  if (item.type === 'textfield' || item.type === 'textarea' || item.type === 'date' || item.type === 'time' || item.type === 'datetime') {
                    return (
                      <Box key={idx} sx={{ flex: 1, minWidth: 180 }}>
                        <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                        <input
                          type={item.type === 'textarea' ? 'textarea' : 'text'}
                          value={item.value}
                          readOnly
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '7px', border: '1px solid #e0e0e0', fontSize: '1rem', background: '#fafafa', color: '#8a1538', fontWeight: 500 }}
                        />
                      </Box>
                    );
                  }
                  // Default fallback
                  return (
                    <Box key={idx} sx={{ flex: 1, minWidth: 180 }}>
                      <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                      <input
                        type="text"
                        value={item.value}
                        readOnly
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '7px', border: '1px solid #e0e0e0', fontSize: '1rem', background: '#fafafa', color: '#8a1538', fontWeight: 500 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })()
        ))}
      </Box>
    )}
    {/* All other sections as cards */}
  {list.slice(list[0] && list[0].header.trim() === '' ? 1 : 0).map((section, idx) => (
      <Box key={idx} sx={{ mt: 3, mx: 3, background: '#fafafd', borderRadius: 3, border: '2px solid #d6dbe3', boxShadow: '0 1px 4px rgba(139,22,52,0.06)', p: 2.2 }}>
        <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1.35rem', mb: 1.5 }}>{section.header.trim()}</Typography>
        <Box sx={{ px: 1 }}>
          {Array.from({ length: Math.ceil(section.items.length / 2) }).map((_, rowIdx) => (
            <Box key={rowIdx} sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
              {section.items.slice(rowIdx * 2, rowIdx * 2 + 2).map((item, itemIdx) => (
                <Box key={itemIdx} sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#607080', fontWeight: 500, fontSize: '1.18rem', mb: 1 }}>{item.title}</Typography>
                  <Box sx={{ background: '#f6f8fc', borderRadius: 3, px: 2, py: 1.5, fontSize: '1.18rem', color: '#8a1538', fontWeight: 600, border: '1.5px solid #e3e7ef', minHeight: 48, display: 'flex', alignItems: 'center' }}>
                    {item.isSignature ? (
                      item.value ? (
                        <img
                          src={item.value}
                          alt="Signature"
                          style={{ width: 180, height: 80, borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fafafa', objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography sx={{ color: '#8a1538', fontWeight: 700, fontSize: '1.05rem' }}>No signature</Typography>
                      )
                    ) : (
                      Array.isArray(item.value) ? (item.value.length > 0 ? item.value.join(', ') : 'None selected') : item.value
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    ))}
    </Box>
  );
}
